import React from "react";
import { Clock, Percent, Hash } from "lucide-react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { makeFormatValue } from "../utils/axisFormat";
import { formatNumber } from "../../../../shared/utils/numberFormatter";

export default function FunnelDataTable({
  steps = [],
  measuring = "conversion_rate",
  breakdowns = [],
  config,
}) {
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const funnelAxis = config?.funnelAxis ?? "left";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);
  // Helper to format time interval
  const formatTimeInterval = (interval) => {
    if (!interval) return '—';
    if (typeof interval === 'string') return interval;
    if (typeof interval === 'object' && interval !== null) {
      const { hours = 0, minutes = 0, seconds = 0 } = interval;
      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 && parts.length === 0) parts.push(`${seconds}s`);
      return parts.length > 0 ? parts.join(' ') : '0s';
    }
    return String(interval);
  };

  // Calculate overall conversion rate (first step to last step)
  const overallConversion =
    steps.length > 1 && steps[0]?.count > 0
      ? ((steps[steps.length - 1]?.count || 0) / steps[0].count) * 100
      : steps.length === 1
      ? 100
      : 0;

  // Use overall conversion from backend if available (more accurate)
  const finalOverallConversion =
    steps.length > 0 && steps[0]?.overallConversion !== undefined
      ? steps[0].overallConversion
      : overallConversion;

  // Helper to format time for display (e.g., "1.8D", "14h")
  const formatTimeDisplay = (interval) => {
    if (!interval) return '—';
    if (typeof interval === 'string') {
      // If already formatted, return as is
      return interval;
    }
    if (typeof interval === 'object' && interval !== null) {
      const { hours = 0, minutes = 0, seconds = 0, days = 0 } = interval;
      if (days > 0) {
        const dayFraction = days + (hours / 24);
        return `${dayFraction.toFixed(1)}D`;
      }
      if (hours > 0) {
        return `${hours}h`;
      }
      if (minutes > 0) {
        return `${minutes}m`;
      }
      return `${seconds}s`;
    }
    return String(interval);
  };

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse" style={{ fontFamily: "Raleway, sans-serif" }}>
        <thead style={{ backgroundColor: '#fafafa' }}>
          {/* Main Header Row */}
          <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
            <th 
              rowSpan={2}
              className="text-left py-2 px-3 text-xs font-semibold uppercase" 
              style={{ color: theme.textMuted, borderRight: `1px solid ${theme.border}` }}
            >
              Property
            </th>
            <th 
              rowSpan={2}
              className="text-left py-2 px-3 text-xs font-semibold uppercase" 
              style={{ color: theme.textMuted, borderRight: `1px solid ${theme.border}` }}
            >
              Total Conv.
            </th>
            {steps.map((step, index) => {
              const stepLabel = step.label || step.eventName || `Step ${index + 1}`;
              return (
                <React.Fragment key={step.id || index}>
                  {/* Step 1: Just one column with # sub-header */}
                  {index === 0 && (
                    <th 
                      colSpan={1}
                      className="text-left py-2 px-3 text-xs font-semibold" 
                      style={{ 
                        color: theme.textMuted,
                        borderRight: index < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                      }}
                    >
                      {index + 1} {stepLabel}
                    </th>
                  )}
                  {/* Step 2+: Main column spans multiple sub-columns */}
                  {index > 0 && (
                    <th 
                      colSpan={measuring === 'time_to_convert' ? 3 : 2}
                      className="text-left py-2 px-3 text-xs font-semibold" 
                      style={{ 
                        color: theme.textMuted,
                        borderRight: index < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                      }}
                    >
                      {index + 1} {stepLabel}
                    </th>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
          {/* Sub-header Row with Icons */}
          <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
            {steps.map((step, index) => {
              return (
                <React.Fragment key={step.id || index}>
                  {/* Step 1: Just # icon */}
                  {index === 0 && (
                    <th 
                      className="text-left py-1 px-3 text-xs" 
                      style={{ 
                        color: theme.textMuted,
                        borderRight: index < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                      }}
                    >
                      <Hash className="w-3 h-3 inline" />
                    </th>
                  )}
                  {/* Step 2+: Time, %, # icons */}
                  {index > 0 && (
                    <>
                      {measuring === 'time_to_convert' && (
                        <th className="text-left py-1 px-3 text-xs" style={{ color: theme.textMuted }}>
                          <Clock className="w-3 h-3 inline" />
                        </th>
                      )}
                      <th className="text-left py-1 px-3 text-xs" style={{ color: theme.textMuted }}>
                        <Percent className="w-3 h-3 inline" />
                      </th>
                      <th 
                        className="text-left py-1 px-3 text-xs" 
                        style={{ 
                          color: theme.textMuted,
                          borderRight: index < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                        }}
                      >
                        <Hash className="w-3 h-3 inline" />
                      </th>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y" style={{ borderColor: theme.border }}>
          {/* Overall Row */}
          <tr>
            {/* Property Column */}
            <td 
              className="py-3 px-3"
              style={{ borderRight: `1px solid ${theme.border}` }}
            >
              <div className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: theme.accentChart || '#7c3aed' }}
                />
                <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>
                  Overall
                </span>
              </div>
            </td>

            {/* Total Conversion Column */}
            <td 
              className="py-3 px-3"
              style={{ borderRight: `1px solid ${theme.border}` }}
            >
              <span className="text-sm" style={{ color: theme.textPrimary }}>
                {formatYValue(finalOverallConversion)}
              </span>
            </td>

            {/* Step Columns */}
            {steps.map((step, index) => {
              const count = step.count || 0;
              const value = step.value;
              const conversion = step.conversion || 0;
              const timeToConvert = step.timeToConvert || (measuring === 'time_to_convert' ? step.value : null);

              // Determine what to display based on measuring type
              const getStep1DisplayValue = () => {
                switch (measuring) {
                  case 'unique_users':
                    return formatNumber(value !== undefined ? value : count);
                  case 'total_conversions':
                  case 'total_sessions':
                  case 'property_sum':
                    return formatNumber(value !== undefined ? value : 0);
                  case 'time_to_convert':
                    return formatTimeDisplay(timeToConvert);
                  case 'conversion_rate':
                  default:
                    return formatNumber(count);
                }
              };

              const getStep2PlusDisplayValue = () => {
                switch (measuring) {
                  case 'unique_users':
                    return formatNumber(value !== undefined ? value : count);
                  case 'total_conversions':
                  case 'total_sessions':
                  case 'property_sum':
                    return formatNumber(value !== undefined ? value : 0);
                  case 'time_to_convert':
                    return formatTimeDisplay(timeToConvert);
                  case 'conversion_rate':
                  default:
                    return formatNumber(count);
                }
              };

              return (
                <React.Fragment key={step.id || index}>
                  {/* Step 1: Just show value/count under # */}
                  {index === 0 && (
                    <td
                      className="py-3 px-3"
                      style={{
                        borderRight:
                          index < steps.length - 1
                            ? `1px solid ${theme.border}`
                            : "none",
                      }}
                    >
                      <span
                        className="text-sm"
                        style={{ color: theme.textPrimary }}
                      >
                        {getStep1DisplayValue()}
                      </span>
                    </td>
                  )}

                  {/* Step 2+: Show Time (if applicable), %, and # */}
                  {index > 0 && (
                    <>
                      {/* Time Column (only for time_to_convert measuring) */}
                      {measuring === 'time_to_convert' && (
                        <td className="py-3 px-3">
                          <span className="text-sm" style={{ color: theme.textPrimary }}>
                            {formatTimeDisplay(timeToConvert)}
                          </span>
                        </td>
                      )}

                      {/* Conversion Rate Column */}
                      <td className="py-3 px-3">
                        <span className="text-sm" style={{ color: theme.textPrimary }}>
                          {formatYValue(conversion)}
                        </span>
                      </td>

                      {/* Count/Value Column */}
                      <td
                        className="py-3 px-3"
                        style={{
                          borderRight:
                            index < steps.length - 1
                              ? `1px solid ${theme.border}`
                              : "none",
                        }}
                      >
                        <span
                          className="text-sm"
                          style={{ color: theme.textPrimary }}
                        >
                          {getStep2PlusDisplayValue()}
                        </span>
                      </td>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>

          {/* Breakdown Rows */}
          {breakdowns &&
            breakdowns.length > 0 &&
            breakdowns.map((breakdown, breakdownIndex) => {
              const breakdownValue =
                breakdown.breakdownValue ||
                breakdown.breakdownKey ||
                "Unknown";
              const overallConversion = breakdown.overallConversion || 0;
            const stepData = breakdown.steps || {};

            // Generate a color for this breakdown segment (cycle through colors)
            const colors = [
              '#7c3aed', '#3b82f6', '#10b981', '#f59e0b', 
              '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'
            ];
            const breakdownColor = colors[breakdownIndex % colors.length];

            return (
              <tr key={breakdownIndex}>
                {/* Property Column */}
                <td 
                  className="py-3 px-3"
                  style={{ borderRight: `1px solid ${theme.border}` }}
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: breakdownColor }}
                    />
                    <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>
                      {breakdownValue}
                    </span>
                  </div>
                </td>

                {/* Total Conversion Column */}
                <td
                  className="py-3 px-3"
                  style={{ borderRight: `1px solid ${theme.border}` }}
                >
                  <span className="text-sm" style={{ color: theme.textPrimary }}>
                    {formatYValue(overallConversion)}
                  </span>
                </td>

                {/* Step Columns */}
                {steps.map((step, stepIndex) => {
                  const stepNum = stepIndex + 1;
                  const stepInfo = stepData[`step${stepNum}`] || {};
                  const count = stepInfo.count || 0;
                  const value = stepInfo.value;
                  const conversionRate = stepInfo.conversionRate || (stepIndex === 0 ? 100.0 : 0.0);
                  const timeToConvert = stepInfo.timeToConvert || (measuring === 'time_to_convert' ? value : null);
                  
                  // Determine what to display based on measuring type
                  const getStep1DisplayValue = () => {
                    switch (measuring) {
                      case 'unique_users':
                        return formatNumber(value !== undefined ? value : count);
                      case 'total_conversions':
                      case 'total_sessions':
                      case 'property_sum':
                        return formatNumber(value !== undefined ? value : 0);
                      case 'time_to_convert':
                        return formatTimeDisplay(timeToConvert);
                      case 'conversion_rate':
                      default:
                        return formatNumber(count);
                    }
                  };

                  const getStep2PlusDisplayValue = () => {
                    switch (measuring) {
                      case 'unique_users':
                        return formatNumber(value !== undefined ? value : count);
                      case 'total_conversions':
                      case 'total_sessions':
                      case 'property_sum':
                        return formatNumber(value !== undefined ? value : 0);
                      case 'time_to_convert':
                        return formatTimeDisplay(timeToConvert);
                      case 'conversion_rate':
                      default:
                        return formatNumber(count);
                    }
                  };

                  return (
                    <React.Fragment key={step.id || stepIndex}>
                      {/* Step 1: Just show value/count under # */}
                      {stepIndex === 0 && (
                        <td 
                          className="py-3 px-3"
                          style={{ 
                            borderRight: stepIndex < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                          }}
                        >
                          <span className="text-sm" style={{ color: theme.textPrimary }}>
                            {getStep1DisplayValue()}
                          </span>
                        </td>
                      )}

                      {/* Step 2+: Show Time (if applicable), %, and # */}
                      {stepIndex > 0 && (
                        <>
                          {/* Time Column (only for time_to_convert measuring) */}
                          {measuring === 'time_to_convert' && (
                            <td className="py-3 px-3">
                              <span className="text-sm" style={{ color: theme.textPrimary }}>
                                {formatTimeDisplay(timeToConvert)}
                              </span>
                            </td>
                          )}

                          {/* Conversion Rate Column */}
                          <td className="py-3 px-3">
                            <span
                              className="text-sm"
                              style={{ color: theme.textPrimary }}
                            >
                              {formatYValue(conversionRate)}
                            </span>
                          </td>

                          {/* Count/Value Column */}
                          <td 
                            className="py-3 px-3"
                            style={{ 
                              borderRight: stepIndex < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                            }}
                          >
                            <span className="text-sm" style={{ color: theme.textPrimary }}>
                              {getStep2PlusDisplayValue()}
                            </span>
                          </td>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
